import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useQuery } from "@apollo/client";
import {
    CZ,
    DE,
    FR,
    GB,
    PL,
    RO,
    SK,
    UA
} from "country-flag-icons/react/3x2";

import { GET_APP_PARAMS } from "graphql/query/local-store-gql";

import { Icons } from "components/layout";
import Localize from "components/service/localize";
import useVarParam from "components/use-hooks/use-var-param";
import {
    getEnabledLanguage,
    getTranslationLanguagesWithStatus,
    LANGUAGE_DEFAULT_VALUE,
    TRANSLATION_LANGUAGES
} from "mock-data/translation/languages";
import { setLanguageOverride } from "components/service/translation-cache";

import "./language-select.scss";


const { Option } = Select;

const languageFlagIcons = {
    en : GB,
    de : DE,
    fr : FR,
    pl : PL,
    ro : RO,
    cs : CZ,
    sk : SK,
    uk : UA
};

const getLanguageShort = ({ displayShort, short }) => displayShort || short;

const renderLanguageFlag = (language) => {
    const FlagIcon = languageFlagIcons[language.slug];

    if (!FlagIcon) {
        return (
            <span
                className="language-select-flag fallback"
                aria-label={ getLanguageShort(language) }
            >
                { getLanguageShort(language) }
            </span>
        );
    }

    return (
        <FlagIcon
            className="language-select-flag"
            aria-label={ getLanguageShort(language) }
        />
    );
};

const renderSelectedLanguage = (language) => (
    <span className="language-select-current">
        { renderLanguageFlag(language) }
    </span>
);

const renderDefaultSelectedLanguage = (language) => (
    <span className="language-select-current default">
        { language && renderLanguageFlag(language) }
        <span className="language-select-current-code">AUTO</span>
    </span>
);

const renderLanguageOption = (language) => (
    <div className="language-select-option">
        { renderLanguageFlag(language) }
        <span className="language-select-option-info">
            <span className="language-select-option-label">{ language.label }</span>
        </span>
    </div>
);

const selectLanguageOverride = (appParams, nextLanguage) => {
    setLanguageOverride(nextLanguage);

    appParams.set({
        translationLanguageOverride: nextLanguage
    });
};

const defaultLanguageOption = (
    <div className="language-select-option default">
        <span className="language-select-option-info">
            <span className="language-select-option-label">
                <span className="language-select-option-code">AUTO</span>
                <Localize>GLOBAL.Language_Default</Localize>
            </span>
        </span>
    </div>
);

const LanguageSelect = React.memo(() => {
    const appParams = useVarParam();
    const [ dropdownOpen, setDropdownOpen ] = useState(false);

    const { data : { appParams : params } = { appParams: appParams.get() } } = useQuery(GET_APP_PARAMS);
    const translationLanguages = params.translationLanguages || [];
    const availableLanguages = getTranslationLanguagesWithStatus(translationLanguages);
    const effectiveLanguageSlug = params.me && getEnabledLanguage(params.me.language_slug, translationLanguages) ?
        params.me.language_slug :
        "en";
    const effectiveLanguage = availableLanguages.find((language) => language.slug === effectiveLanguageSlug) ||
        TRANSLATION_LANGUAGES.find((language) => language.slug === "en");

    const selectedOverride = params.translationLanguageOverride;
    const overrideIsValid = !selectedOverride ||
        !!getEnabledLanguage(selectedOverride, translationLanguages);
    const value = overrideIsValid ? (selectedOverride || LANGUAGE_DEFAULT_VALUE) : LANGUAGE_DEFAULT_VALUE;
    const selectModeClass = value === LANGUAGE_DEFAULT_VALUE ? "is-auto" : "is-manual";

    useEffect(() => {
        if (overrideIsValid) return;

        selectLanguageOverride(appParams, null);
    }, [ overrideIsValid, appParams ]);

    const onChange = (selectedLanguage) => {
        const nextLanguage = selectedLanguage === LANGUAGE_DEFAULT_VALUE ? null : selectedLanguage;

        setDropdownOpen(false);
        selectLanguageOverride(appParams, nextLanguage);
    };

    const toggleDropdownByArrow = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDropdownOpen((open) => !open);
    };

    return (
        <div className="language-select-wrap">
            <Select
                className={ `language-select ${ selectModeClass }` }
                dropdownClassName="language-select-dropdown"
                dropdownMatchSelectWidth={ false }
                optionLabelProp="label"
                listHeight={ 290 }
                aria-label="Language"
                open={ dropdownOpen }
                onDropdownVisibleChange={ setDropdownOpen }
                suffixIcon={
                    <span
                        className="language-select-arrow-hit"
                        onMouseDown={ toggleDropdownByArrow }
                    >
                        <Icons.Arrow type="filled" />
                    </span>
                }
                value={ value }
                onChange={ onChange }
            >
                <Option
                    value={ LANGUAGE_DEFAULT_VALUE }
                    label={ renderDefaultSelectedLanguage(effectiveLanguage) }
                >
                    { defaultLanguageOption }
                </Option>
                { availableLanguages.map((language) => (
                    <Option
                        value={ language.slug }
                        key={ language.slug }
                        label={ renderSelectedLanguage(language) }
                        title={ `${ getLanguageShort(language) } - ${ language.label }` }
                        disabled={ language.status === false }
                    >
                        { renderLanguageOption(language) }
                    </Option>
                )) }
            </Select>
        </div>
    );
});

export default LanguageSelect;
